<template>
  <IonApp class="global-app">
    <template v-if="isAuthLoading">
      <EmnLoader />
    </template>

    <template v-else-if="!isAuthenticated">
      <Suspense>
        <StoryblokPreview v-if="isStoryblokPreviewRoute" />
        <LoginPage v-else />
      </Suspense>
    </template>

    <template v-else>
      <NavigationLayout />
      <ContentLayout>
        <IonRouterOutlet id="main-content" :key="$route.path" />
      </ContentLayout>
    </template>

    <GlobalErrorModal />
    <EMToast
      v-model="toastState.visible"
      :message="toastState.message"
      :type="toastState.type"
      @update:model-value="hideToast"
    />
  </IonApp>
</template>

<script setup lang="ts">
import { computed, provide, onMounted, ref } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { useTheming, type ThemingContext } from '@/common/composables/useTheming';
import { useRoute } from 'vue-router';
import { useUser } from '@/common/composables/useUser';
import { authService } from '@/features/common/auth/services/authService';

import NavigationLayout from '@/layouts/navigation-layout/NavigationLayout.vue';
import LoginPage from '@/features/common/auth/views/LoginPage.vue';
import ContentLayout from '@/layouts/ContentLayout.vue';
import EmnLoader from '@/common/components/loader/EmnLoader.vue';
import GlobalErrorModal from '@/common/components/ui/modal/GlobalErrorModal.vue';
import EMToast from '@/common/components/ui/toast/EMToast.vue';
import { useToast } from '@/common/composables/useToast';
import { StoryblokPreview } from '@/features/storyblok';

const { toastState, hideToast } = useToast();
const theming = useTheming();
const $route = useRoute();
const { isAuthenticated } = useUser();
const isAuthLoading = ref(true);
const isStoryblokIframe =
  window.location.ancestorOrigins &&
  window.location.ancestorOrigins.contains('https://app.storyblok.com');
const isStoryblokPreviewRoute = computed(
  () => $route.path === '/storyblok-preview' && isStoryblokIframe
);
provide<ThemingContext>('theme', theming);

onMounted(async () => {
  await authService.waitForInitialization();
  isAuthLoading.value = false;
  // Remove the HTML splash screen
  document.getElementById('app-splash')?.remove();
});
// Provide theming context for legacy components that use inject('theme')
</script>

<style scoped>
.global-app {
  background: var(--ion-background-layout);
}
</style>
